import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
import MetricsInput from './MetricsInput';
import DimensionsInput from './DimensionsInput';
import StackedBarOptionsInput from './StackedBarOptionsInput';
import StackedAreaOptionsInput from './StackedAreaOptionsInput';

import SimpleGridConfigInput from './SimpleGridConfigInput';
import ErrorBoundary from '../Common/ErrorBoundary';
import Actions from './Actions';
import CardContext from '../../contexts/CardContext';
import VisualisationUsageDetails from '../VisualisationUsageDetails';
import V5Gadget from '../V5Gadget';
import { Section } from '../MyAccount/Profile/styles';
import Typography from 'kingpin/atoms/Typography';
import Inputs from '../Inputs';
import toSentenceCase from '../../services/toSentenceCase';
import Label from './Label';
import DateInput from '../DateInput';
import DataLabelsInput from './DataLabelsInput';
import ReverseAxesInput from './ReverseAxesInput';
import TotalsColumnPositionInput from './TotalsColumnPositionInput';
import StackSecondaryRowsInput from './StackSecondaryRowsInput';
import Row from '../Common/Row';
import GroupByLimitModeInput from './GroupByLimitModeInput';
import LimitInput from './LimitInput';
import ExcludeInputs from './ExcludeInputs';
import Dropdown from '../Inputs/Dropdown';
import SortOptions from './SortOptions';
import { InputWrapper } from './styles';

export const GadgetFormWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: var(--color-bg-primary);
  border-radius: 3px;
`;

export const GadgetFormInner = styled.div<{ isGadgetBuilder: boolean }>`
  flex: 1;
  flex-direction: column;
  height: 100%;
  padding: ${(props) => (props.isGadgetBuilder ? '0px' : '12px 24px')};

  ${(props) => props.isGadgetBuilder && `overflow-y: auto; height: 100%;`}

  border-right: 1px solid var(--color-border-secondary);
`;

export const GadgetFormInnerContent = styled.div<{ isGadgetBuilder: boolean }>`
  padding: ${(props) => (props.isGadgetBuilder ? '0px 12px' : '0px')};
`;

export const GadgetFormPreview = styled.div`
  flex: 2;
  flex-direction: column;
  padding: 8px;
  overflow-y: hidden;
  position: relative;
  display: flex;
  height: 100%;
`;

const V5GadgetForm = ({
  name,
  onNameChange,
  description,
  onDescriptionChange,
  gadgetType,
  isShowingSortInputs,
  isShowingGroupingInputs,
  groupByLimit,
  onGroupByLimitChange,
  isShowingTrendingInputs,
  trendingMode,
  trendingModeOptions,
  trendByCalendarInterval,
  trendByFiscalInterval,
  onTrendByCalendarIntervalChange,
  excludePartialIntervals,
  onExcludePartialIntervalsChange,
  trendByFixedIntervalDays,
  onTrendByFixedIntervalChange,
  onTrendByFiscalIntervalChange,
  draftChart,
  isDimensionalChart,
  isValidChart,
  gridConfig,
  setGridConfig,
  isGadgetBuilder,
  gridSizingOptions,
}: {
  name: string;
  onNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  description: string;
  onDescriptionChange: (event: ChangeEvent<HTMLInputElement>) => void;
  gadgetType: GadgetType;
  onGadgetTypeChange: (newType: GadgetType) => void;
  isShowingSortInputs: boolean;
  isShowingGroupingInputs: boolean;
  groupByLimit?: number;
  onGroupByLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isShowingTrendingInputs: boolean;
  trendingMode:
    | 'calendarInterval'
    | 'fixedInterval'
    | 'hourInterval'
    | 'fiscalInterval';
  trendingModeOptions: RadioOption[];
  trendByCalendarInterval?: FleetOps.Interval | 'auto';
  trendByFiscalInterval?: FixedPeriods.FiscalType;
  onTrendByCalendarIntervalChange: (
    newValue: FleetOps.Interval | 'auto',
  ) => void;
  excludePartialIntervals: boolean;
  onExcludePartialIntervalsChange: (newValue: boolean) => void;
  trendByFixedIntervalDays?: number;
  onTrendByFixedIntervalChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onTrendByFiscalIntervalChange: (newValue: FixedPeriods.FiscalType) => void;
  draftChart?: V5ChartDefinition;
  isDimensionalChart: boolean;
  isValidChart: boolean;
  gridConfig?: SimpleGridConfig;
  setGridConfig: React.Dispatch<
    React.SetStateAction<SimpleGridConfig | undefined>
  >;
  isGadgetBuilder: boolean;
  gridSizingOptions: DropdownOption[];
}) => (
  <GadgetFormWrapper>
    <GadgetFormInner isGadgetBuilder={isGadgetBuilder}>
      <GadgetFormInnerContent isGadgetBuilder={isGadgetBuilder}>
        <Section>
          <Label>Name</Label>
          <Inputs.TextInput
            placeholder="Name"
            value={name}
            onChange={onNameChange}
            inputSize="Small"
            width="100%"
          />
        </Section>
        <Section>
          <Label>Description</Label>
          <Inputs.TextInput
            placeholder="Description"
            value={description}
            onChange={onDescriptionChange}
            inputSize="Small"
            width="100%"
          />
        </Section>
        {gadgetType === 'matrix' && (
          <Section>
            <Label>Column Sizing</Label>
            <Dropdown options={gridSizingOptions} />
          </Section>
        )}
        {gadgetType === 'matrix' && (
          <Section>
            <ReverseAxesInput />
          </Section>
        )}
        {gadgetType === 'matrix' && (
          <Section>
            <TotalsColumnPositionInput />
          </Section>
        )}
        {gadgetType === 'matrix' && (
          <Section>
            <StackSecondaryRowsInput />
          </Section>
        )}

        <DataLabelsInput />
        <StackedBarOptionsInput />
        <Section>
          <StackedAreaOptionsInput />
        </Section>
        {gadgetType !== 'simpleGrid' && (
          <Section>
            <Typography.Header type="H5">Metrics</Typography.Header>
            <MetricsInput />
          </Section>
        )}

        {gadgetType === 'simpleGrid' && !!gridConfig && (
          <SimpleGridConfigInput
            gridConfig={gridConfig}
            setGridConfig={setGridConfig}
          />
        )}
        {isDimensionalChart && (
          <Section>
            <Typography.Header type="H5">Dimensions</Typography.Header>
            <ErrorBoundary>
              <DimensionsInput />
            </ErrorBoundary>
          </Section>
        )}
        {isShowingSortInputs && (
          <Section>
            <div
              style={{ padding: 12, backgroundColor: 'var(--color-bg-muted)' }}
            >
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>
                  <Typography.Body type="Label">Sort</Typography.Body>
                </div>
                <SortOptions />
              </div>
            </div>
          </Section>
        )}
        {isShowingGroupingInputs && (
          <Section>
            <Typography.Header type="H5">Grouping</Typography.Header>
            <div
              style={{ padding: 12, backgroundColor: 'var(--color-bg-muted)' }}
            >
              <GroupByLimitModeInput />
              <LimitInput
                groupByLimit={groupByLimit}
                onGroupByLimitChange={onGroupByLimitChange}
              />
              <ExcludeInputs />
            </div>
          </Section>
        )}
        {isShowingTrendingInputs && (
          <Section>
            <Typography.Header type="H5">Trending</Typography.Header>
            <div
              style={{ padding: 12, backgroundColor: 'var(--color-bg-muted)' }}
            >
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>
                  <Typography.Body type="Label">Mode</Typography.Body>
                </div>
                <Inputs.Radio options={trendingModeOptions} />
              </div>
              <React.Fragment>
                {trendingMode === 'calendarInterval' && (
                  <React.Fragment>
                    <InputWrapper>
                      <Label>Interval</Label>
                      <Inputs.Dropdown
                        selectedLabel={trendByCalendarInterval}
                        options={[
                          'auto',
                          'year',
                          'quarter',
                          'month',
                          'week',
                          'day',
                          'hour',
                        ].map((o) => ({
                          label: o,
                          onSelected: () =>
                            onTrendByCalendarIntervalChange(
                              o as FleetOps.Interval | 'auto',
                            ),
                        }))}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <Label>Exclude partial intervals</Label>
                      <Inputs.Toggle
                        value={excludePartialIntervals}
                        onChange={onExcludePartialIntervalsChange}
                      />
                    </InputWrapper>
                  </React.Fragment>
                )}
                {trendingMode === 'fiscalInterval' && (
                  <>
                    <InputWrapper>
                      <Label>Interval</Label>
                      <Inputs.Dropdown
                        selectedLabel={trendByFiscalInterval}
                        options={[
                          'fiscalPeriod',
                          'fiscalQuarter',
                          'fiscalYear',
                        ].map((o: string) => ({
                          label: toSentenceCase(o),
                          onSelected: () =>
                            onTrendByFiscalIntervalChange(o as FiscalInterval),
                        }))}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <Label>Exclude partial intervals</Label>
                      <Inputs.Toggle
                        value={excludePartialIntervals}
                        onChange={onExcludePartialIntervalsChange}
                      />
                    </InputWrapper>
                  </>
                )}
                {trendingMode === 'fixedInterval' && (
                  <InputWrapper>
                    <Label>Days</Label>
                    <Inputs.TextInput
                      type="number"
                      value={
                        trendByFixedIntervalDays
                          ? trendByFixedIntervalDays.toString(10)
                          : ''
                      }
                      onChange={onTrendByFixedIntervalChange}
                    />
                  </InputWrapper>
                )}
              </React.Fragment>
            </div>
          </Section>
        )}

        {isGadgetBuilder && <Actions />}
      </GadgetFormInnerContent>
    </GadgetFormInner>
    {isGadgetBuilder && (
      <>
        <GadgetFormPreview>
          <Row spaceBetween>
            <DateInput />
            {draftChart && (
              <VisualisationUsageDetails chatDefinition={draftChart} />
            )}
          </Row>
          {draftChart && (
            <>
              {(draftChart.series.length > 0 ||
                (!!draftChart.simpleGridConfig &&
                  draftChart.simpleGridConfig.fields.length > 0)) &&
                isValidChart && (
                  <ErrorBoundary>
                    <CardContext.Provider
                      // @ts-ignore
                      value={{ chartDefinition: draftChart }}
                    >
                      <V5Gadget
                        chartDefinition={draftChart}
                        key={
                          draftChart.gadgetType === 'matrix'
                            ? JSON.stringify(draftChart)
                            : undefined
                        } // Ensure fresh
                      />
                    </CardContext.Provider>
                  </ErrorBoundary>
                )}
            </>
          )}
        </GadgetFormPreview>
      </>
    )}
  </GadgetFormWrapper>
);

export default V5GadgetForm;
